/**
 * Cart Drawer Recommendation
 * Muestra el producto más recomendado basado en todos los productos del carrito
 */

class CartDrawerRecommendation {
  constructor() {
    console.log('CartDrawerRecommendation: constructor called');
    this.container = document.getElementById('cart-drawer-recommendation');
    this.contentContainer = document.getElementById('cart-drawer-recommendation-content');

    console.log('Container:', this.container);
    console.log('Content container:', this.contentContainer);

    if (!this.container || !this.contentContainer) {
      console.error('CartDrawerRecommendation: Required elements not found');
      return;
    }

    this.init();
  }

  init() {
    console.log('CartDrawerRecommendation: init() called');

    // Actualizar cuando el carrito cambia
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      console.log('Subscribing to cartUpdate events');
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        console.log('cartUpdate event received');
        this.update();
      });
    } else {
      console.warn('PubSub not available');
    }

    // Actualizar cuando se abre el drawer
    document.addEventListener('cart:drawer-opened', () => {
      console.log('cart:drawer-opened event received');
      this.update();
    });

    // Actualizar al cargar la página
    console.log('Initial update on load');
    this.update();
  }

  async update() {
    console.log('CartDrawerRecommendation: update() called');
    try {
      // Obtener carrito actual
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      console.log('Cart data:', cartData);

      if (!cartData.items || cartData.items.length === 0) {
        console.log('Cart is empty, hiding recommendation');
        this.hide();
        return;
      }

      // Obtener el producto más recomendado
      const recommendedProduct = await this.getMostRecommendedProduct(cartData.items);

      if (recommendedProduct) {
        console.log('Rendering recommended product:', recommendedProduct.title);
        this.render(recommendedProduct);
        this.show();
      } else {
        console.log('No recommended product found, hiding');
        this.hide();
      }
    } catch (error) {
      console.error('Error updating drawer recommendation:', error);
      this.hide();
    }
  }

  async getMostRecommendedProduct(cartItems) {
    const cartProductIds = cartItems.map(item => item.product_id);
    const recommendationCounts = {};

    console.log('Cart products:', cartItems.map(i => i.handle));

    // Para cada producto del carrito, obtener recomendaciones usando Shopify Recommendations API
    for (const item of cartItems) {
      try {
        // Usar el API nativo de Shopify para recomendaciones
        const response = await fetch(`/recommendations/products.json?product_id=${item.product_id}&limit=10`);

        if (!response.ok) {
          console.log(`No recommendations for product ${item.product_id}`);
          continue;
        }

        const data = await response.json();

        if (!data.products || data.products.length === 0) {
          console.log(`Empty recommendations for ${item.handle}`);
          continue;
        }

        console.log(`Found ${data.products.length} recommendations for ${item.handle}`);

        // Contar cada producto recomendado por su handle
        for (const product of data.products) {
          const handle = product.handle;
          if (!recommendationCounts[handle]) {
            recommendationCounts[handle] = {
              count: 0,
              product: product
            };
          }
          recommendationCounts[handle].count++;
        }
      } catch (error) {
        console.error(`Error fetching recommendations for ${item.handle}:`, error);
      }
    }

    console.log('Recommendation counts:', Object.keys(recommendationCounts).map(h => ({
      handle: h,
      count: recommendationCounts[h].count
    })));

    // Ordenar por count (mayor a menor)
    const sortedRecommendations = Object.entries(recommendationCounts)
      .sort((a, b) => b[1].count - a[1].count);

    console.log('Sorted recommendations:', sortedRecommendations.map(([h, data]) => ({
      handle: h,
      count: data.count
    })));

    // Buscar el primero que NO esté en el carrito
    for (const [handle, data] of sortedRecommendations) {
      const product = data.product;

      if (!cartProductIds.includes(product.id)) {
        console.log(`Selected product: ${handle} (count: ${data.count})`);
        return product;
      } else {
        console.log(`Skipping ${handle} - already in cart`);
      }
    }

    console.log('No suitable recommendation found');
    return null;
  }

  render(product) {
    const priceHTML = this.formatPrice(product);

    this.contentContainer.innerHTML = `
      <a href="${product.url}" class="cart-drawer-recommendation__product">
        <img
          src="${product.featured_image}"
          alt="${this.escapeHtml(product.title)}"
          class="cart-drawer-recommendation__image"
        >
        <div class="cart-drawer-recommendation__info">
          <div class="cart-drawer-recommendation__product-title">${this.escapeHtml(product.title)}</div>
          <div class="cart-drawer-recommendation__price">${priceHTML}</div>
        </div>
        <svg class="cart-drawer-recommendation__arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    `;
    const card = this.contentContainer.querySelector('.cart-drawer-recommendation__product');
    if (card) {
      card.dataset.expressRecommendationUrl = product.url;
      window.expressUI?.attachCardBadge(card, product.url);
    }
  }

  formatPrice(product) {
    const price = product.price / 100;
    const comparePrice = product.compare_at_price ? product.compare_at_price / 100 : null;
    const hasVariantPrices = product.price_min !== product.price_max;

    // Obtener settings de promo
    const promoEnabled = document.body.dataset.promoEnabled === 'true';
    const promoDiscount = parseFloat(document.body.dataset.promoDiscount || '1');

    // Obtener texto "Desde" según idioma
    // Idioma base: la tienda sirve pt-PT y las claves de abajo son de dos letras.
    const locale = (document.documentElement.lang || 'es').split('-')[0];
    const fromText = {
      'es': 'Desde',
      'en': 'From',
      'de': 'Ab',
      'fr': 'À partir de',
      'it': 'Da',
      'pt': 'Desde'
    }[locale] || 'From';

    let priceText = `${price.toFixed(2)}€`;

    // Añadir "Desde" si tiene múltiples precios
    if (hasVariantPrices) {
      priceText = `<span class="price-from">${fromText} </span>${priceText}`;
    }

    // Aplicar descuento de promo si está activo
    if (promoEnabled && promoDiscount < 1) {
      const discountedPrice = (price * promoDiscount).toFixed(2);
      const originalPrice = comparePrice ? comparePrice.toFixed(2) : price.toFixed(2);

      if (hasVariantPrices) {
        priceText = `<span class="price-from">${fromText} </span><span class="price-current">${discountedPrice}€</span> <span class="price-original">${originalPrice}€</span>`;
      } else {
        priceText = `<span class="price-current">${discountedPrice}€</span> <span class="price-original">${originalPrice}€</span>`;
      }
    }

    return priceText;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  show() {
    this.container.style.display = 'block';
  }

  hide() {
    this.container.style.display = 'none';
  }
}

// Inicializar cuando el DOM esté listo
function initCartDrawerRecommendation() {
  console.log('Attempting to initialize CartDrawerRecommendation');

  const container = document.getElementById('cart-drawer-recommendation');
  const contentContainer = document.getElementById('cart-drawer-recommendation-content');

  if (container && contentContainer) {
    console.log('Elements found, creating instance');
    new CartDrawerRecommendation();
  } else {
    console.log('Elements not found yet, will retry in 500ms');
    setTimeout(initCartDrawerRecommendation, 500);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawerRecommendation);
} else {
  initCartDrawerRecommendation();
}
